const MAX_DIMENSION = 4096;

/**
 * Resizes an image blob so that neither dimension exceeds maxDimension, preserving
 * aspect ratio. Returns the original blob unchanged for non-image types, when the
 * image already fits, or when the Canvas API is unavailable (graceful degradation).
 *
 * @param blob - The image data to potentially resize
 * @param maxDimension - Maximum allowed width or height in pixels (default 4096)
 */
export default async function resizeImageIfNeeded(
  blob: Blob,
  maxDimension = MAX_DIMENSION,
): Promise<Blob> {
  if (!blob.type.startsWith('image/')) {
    return blob;
  }

  try {
    return await new Promise<Blob>((resolve) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);

        try {
          const { naturalWidth, naturalHeight } = img;
          if (naturalWidth <= maxDimension && naturalHeight <= maxDimension) {
            resolve(blob);
            return;
          }

          const scale = Math.min(maxDimension / naturalWidth, maxDimension / naturalHeight);
          const targetWidth = Math.round(naturalWidth * scale);
          const targetHeight = Math.round(naturalHeight * scale);

          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          const ctx = canvas.getContext('2d');
          if (!ctx || typeof canvas.toBlob !== 'function') {
            resolve(blob);
            return;
          }

          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Preserve the original MIME type for common formats; fall back to jpeg
          const outputType = ['image/jpeg', 'image/png', 'image/webp'].includes(blob.type)
            ? blob.type
            : 'image/jpeg';

          canvas.toBlob(
            (resizedBlob) => resolve(resizedBlob ?? blob),
            outputType,
            0.92,
          );
        } catch {
          // Graceful degradation: if any canvas operation fails, upload the original
          resolve(blob);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };

      img.src = url;
    });
  } catch {
    // Graceful degradation: if Canvas API fails for any reason, upload original
    return blob;
  }
}
